import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api";
import AccessibilityPanel from "../components/AccessibilityPanel";
import { G_DARK, Field, Input, PasswordInput, Btn, globalStyles } from "../components/Shared";

// Where a successful sign-in goes. `next` is set by ProtectedRoute and by the
// api.js fallback to the page a signed-out visitor asked for — since
// 2026-09-24 that is typically a quotation to sign, linked from the mail.
// Followed only when it is a path on this site: one leading "/", then not a
// second "/" or a backslash (a browser reads "//host" and "/\host" as another
// host), no backslash or control character anywhere (a browser drops a tab or
// newline and would read "/<tab>/host" the same way), and so no scheme.
// Anything else is an open redirect with the portal's name on the front, so it
// falls back to the project list rather than being guessed at.
export function safeNext(next) {
  if (typeof next !== "string") return "/projects";
  // eslint-disable-next-line no-control-regex
  if (!/^\/(?![/\\])/.test(next) || /[\\\u0000-\u001f\u007f]/.test(next)) return "/projects";
  return next;
}

// Set by AuthContext.logout, cleared by the next sign-in. The URL cannot carry
// the sign-out through Keycloak: `post_logout_redirect_uri` is matched against
// the URI registered on the client and has to stay exactly `/login`.
const SIGNED_OUT_KEY = "portal_signed_out";

function signedOutMarked() {
  try { return sessionStorage.getItem(SIGNED_OUT_KEY) === "1"; } catch { return false; }
}

const SR_ONLY = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden",
                  clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 };

// The links an admin keeps in Management -> Sign-in page links (2026-11-04),
// from the public /auth/config: manuals, tutorials, a wiki — what somebody
// needs before they can sign in. They replaced the one PORTAL_GUIDE_URL link.
// The server has checked every address is an absolute http(s) link; each value
// is rendered as text, never as HTML. Nothing at all for an empty list.
export function HelpLinks({ links }) {
  if (!Array.isArray(links) || links.length === 0) return null;
  return (
    <section aria-labelledby="login-help-links" style={{ marginTop: 24, textAlign: "left" }}>
      <h2 id="login-help-links" style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>
        Help and documentation
      </h2>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        {links.map((l, i) => (
          <li key={i} style={{ minWidth: 0, position: "relative" }}>
            <a href={l.url} target="_blank" rel="noopener noreferrer"
               style={{ display: "inline-flex", alignItems: "center", minHeight: 44, fontSize: 14,
                        fontWeight: 600, color: G_DARK, overflowWrap: "anywhere" }}>
              {l.title}<span style={SR_ONLY}> (opens in a new tab)</span>
            </a>
            {l.description && (
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#4b5563", overflowWrap: "anywhere", whiteSpace: "pre-line" }}>
                {l.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function LoginPage() {
  const { user, login }   = useAuth();
  const navigate          = useNavigate();
  const [params]          = useSearchParams();
  const next              = safeNext(params.get("next"));
  const [links, setLinks] = useState([]);
  // Keycloak (K2): whether the button is offered, what it is called, and
  // whether the password form is still a way in. `local_login` is the
  // server's `on | off` (2026-09-28); until the config answers, the form stays,
  // as it did before there was a choice, and only `off` hides it.
  const [oidc, setOidc] = useState({ enabled: false, name: "", localLogin: "on" });
  // A refusal from the callback arrives as a sentence in the query, because
  // the callback is a redirect and has no page of its own to say it on.
  const oidcError = params.get("oidc_error");
  // The way out of a refusal is offered only where it can work: not while
  // Keycloak is switched off, and not under the logout's own failure
  // (`oidc_logout=failed`), where the same click would fail the same way.
  const offerSignOut = !!oidcError && params.get("oidc_logout") !== "failed";
  const [form, setForm]   = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);
  // After a sign-out the page forwards nobody (SIGNOUT, 2026-09-29): on dev a
  // phone's Back went from here straight into /projects, signed in, because a
  // replayed Keycloak sign-in had put a user back and this page followed it.
  // `?signed_out=1` from the top bar, or the mark that survives the trip to
  // Keycloak; a page restored from the back-forward cache re-reads the mark,
  // since it comes back with the state it had before the sign-out.
  const [signedOut, setSignedOut] = useState(
    () => params.get("signed_out") === "1" || signedOutMarked());

  useEffect(() => {
    const onPageShow = e => { if (e.persisted && signedOutMarked()) setSignedOut(true); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    if (user && !signedOut) { navigate(next, { replace: true }); return; }
    api.get("/auth/config").then(c => {
      setLinks(Array.isArray(c.links) ? c.links : []);
      setOidc({ enabled: !!c.oidc_enabled, name: c.oidc_display_name || "Keycloak",
                localLogin: c.local_login || "on" });
    }).catch(() => {});
  }, [user, signedOut, navigate, next]);

  // A full navigation, not a fetch, and a `replace`: an /api/auth/oidc/* URL
  // left in the history is one Back away from a sign-in Keycloak answers
  // silently from a live SSO session (SIGNOUT, 2026-09-29). the browser follows the backend's redirect
  // to Keycloak and back, and the SPA never holds a Keycloak token — only the
  // portal's own, handed to /login/complete in a fragment. `next` is the one
  // the form would follow, already through safeNext.
  // After a refusal, `prompt=login` makes Keycloak ask for credentials rather
  // than replay the SSO session that was just refused (KC-OUT, 2026-09-25:
  // six identical refusals on dev, one `session_state`). Without a refusal
  // the URL is what it always was.
  const signInWithOidc = () =>
    window.location.replace(`/api/auth/oidc/login?next=${encodeURIComponent(next)}`
                           + (oidcError ? "&prompt=login" : ""));

  // The way out of that refusal: end the Keycloak session. Also a full
  // navigation — the backend redirects to Keycloak's end_session_endpoint,
  // which returns to /login. A button, not an <a href>: apiNavigation.test.js
  // refuses any href to an /api/ path, and this one needs no portal token.
  const signOutOfOidc = () => window.location.replace("/api/auth/oidc/logout");

  // Off hides the password form; the Keycloak button is then the only way in.
  const showForm = oidc.localLogin !== "off";

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(form.email, form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>

      <div style={{ maxWidth: 400, width: "100%" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/csm_binf.png" alt="BOKU Core Facility Bioinformatics" style={{ height: 60, borderRadius: 12, marginBottom: 16 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>Project Portal</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "#6b7280" }}>BOKU Core Facility Bioinformatics</p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          {(oidc.enabled || oidcError) && (
            <div style={{ padding: showForm ? "28px 28px 0" : 28, display: "flex", flexDirection: "column", gap: 14 }}>
              {signedOut && oidc.enabled && (
                <div role="status" style={{ padding: "10px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151", fontSize: 13 }}>
                  You are signed out of the portal. If {oidc.name || "Keycloak"} did not ask you
                  to confirm signing out, it may still be signed in on this device. The next
                  sign-in will ask for your password.
                </div>
              )}
              {oidcError && (
                <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
                  {oidcError}
                </div>
              )}
              {offerSignOut && oidc.enabled && (
                <button type="button" onClick={signOutOfOidc}
                        style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                 fontFamily: "inherit", fontSize: 13, color: G_DARK, fontWeight: 500,
                                 textDecoration: "underline", textAlign: "center" }}>
                  Sign out of {oidc.name || "Keycloak"} / use a different account
                </button>
              )}
              {oidc.enabled && (
                <Btn onClick={signInWithOidc} style={{ width: "100%", justifyContent: "center", padding: "11px 0" }}>
                  Sign in with {oidc.name}
                </Btn>
              )}
              {oidc.enabled && showForm && (
                <div aria-hidden="true" style={{ textAlign: "center", fontSize: 12, color: "#6b7280" }}>or</div>
              )}
            </div>
          )}

          {/* Form */}
          {showForm && (
          <form onSubmit={handleSubmit} style={{ padding: 28, display: "flex", flexDirection: "column", gap: 18 }}>
            {/* The account is its e-mail since 2026-09-28. The autofill token
                stays: it is HTML's name for "the account identifier", and what
                a password manager keys a saved sign-in on. */}
            <Field label="E-mail" required>
              <Input
                autoFocus
                type="email"
                autoComplete="username"
                placeholder="you@example.org"
                value={form.email}
                onChange={set("email")}
              />
            </Field>
            <Field label="Password" required>
              <PasswordInput
                autoComplete="current-password"
                placeholder="••••••••"
                value={form.password}
                onChange={set("password")}
              />
            </Field>

            {error && (
              <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
                {error}
              </div>
            )}

            <Btn type="submit" disabled={busy || !form.email || !form.password} style={{ width: "100%", justifyContent: "center", padding: "11px 0" }}>
              {busy ? "Signing in…" : "Sign in"}
            </Btn>

            <Link to="/forgot" style={{ fontSize: 13, color: G_DARK, textDecoration: "none", fontWeight: 500, textAlign: "center" }}>
              Forgot your password?
            </Link>
          </form>
          )}
        </div>

        {/* Deliberately on the sign-in screen and not only behind the login:
            somebody who needs larger text or higher contrast needs it to read
            this page, not the one after it. */}
        <div style={{ textAlign: "center", marginTop: 20, display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setA11yOpen(true)}
            style={{
              background: "#fff", border: "1px solid #9ca3af", borderRadius: 8,
              padding: "8px 16px", fontSize: 13, fontWeight: 500, color: "#374151",
              cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <span aria-hidden="true">♿</span> Accessibility settings
          </button>
        </div>

        <HelpLinks links={links} />
      </div>

      {a11yOpen && <AccessibilityPanel onClose={() => setA11yOpen(false)} statementPath="/accessibility" />}
    </div>
  );
}
